/**
 * Work Log 상세 보기 컴포넌트
 */

import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { WorkLog, STATUS_LABELS } from '../../constants/workLog'
import { formatDateTime, formatDuration, getStatusColor, getCategoryColor } from '../../utils/workLogHelpers'
import { useCompleteWorkLog, useDeleteWorkLog } from '../../hooks/useWorkLogs'
import { WorkLogContent } from './WorkLogContent'

interface WorkLogDetailProps {
  workLog: WorkLog
  onBack: () => void
  onUpdate: () => void
}

export function WorkLogDetail({ workLog, onBack, onUpdate }: WorkLogDetailProps) {
  const { completeWorkLog, completing } = useCompleteWorkLog()
  const { deleteWorkLog, deleting } = useDeleteWorkLog()

  const handleComplete = async () => {
    const success = await completeWorkLog(workLog.id)

    if (success) {
      onUpdate()
      onBack()
    } else {
      alert('Work Log 완료 처리에 실패했습니다.')
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('이 Work Log를 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.')) {
      return
    }

    const success = await deleteWorkLog(workLog.id)

    if (success) {
      onUpdate()
      onBack()
    } else {
      alert('Work Log 삭제에 실패했습니다.')
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card>
        <div className="p-6">
          <Button onClick={onBack} className="mb-4">
            ← 목록으로
          </Button>

          {/* 상태 및 카테고리 뱃지 */}
          <div className="flex gap-2 mb-4">
            <span className={'inline-block px-3 py-1 rounded-full text-sm font-semibold ' + getStatusColor(workLog.status)}>
              {STATUS_LABELS[workLog.status]}
            </span>
            <span className={'inline-block px-3 py-1 rounded-full text-sm font-semibold ' + getCategoryColor(workLog.category)}>
              {workLog.category}
            </span>
          </div>

          {/* 제목 및 고객명 */}
          <h1 className="text-3xl font-bold mb-2 text-gray-900 dark:text-gray-100">{workLog.title}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">{workLog.customerName}</p>

          {/* 시간 정보 그리드 */}
          <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">시작 시간</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{formatDateTime(workLog.startedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">종료 시간</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{formatDateTime(workLog.finishedAt)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">소요 시간</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{formatDuration(workLog.durationSeconds)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">예상 시간</p>
              <p className="font-semibold text-gray-900 dark:text-gray-100">{workLog.expectedMinutes ? `${workLog.expectedMinutes}분` : 'N/A'}</p>
            </div>
          </div>

          {/* 작업 내용 (Tiptap 등으로 저장된 HTML / 레거시 평문 모두 처리) */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">작업 내용</p>
            <WorkLogContent html={workLog.description} />
          </div>

          {/* 액션 버튼 */}
          <div className="flex gap-2">
            {workLog.status === 'IN_PROGRESS' && (
              <Button
                onClick={handleComplete}
                className="bg-green-600 text-white hover:bg-green-700"
                disabled={completing || deleting}
              >
                {completing ? '처리 중...' : '완료 처리'}
              </Button>
            )}
            <Button
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={completing || deleting}
            >
              {deleting ? '삭제 중...' : '삭제'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
